import React, { useState, useEffect, useCallback } from 'react';
import './emissions.css'
import * as XLSX from 'xlsx';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Company, Emissions, Vessels } from '../../interfaces/interfaces';
import { getCompany, getVessel, postEmissions } from '../../services/requests';
import { useTheme } from '../../themeContext';
import { ChartOptionsBuilder } from '../../components/graphics/chartOptions';
import ReactECharts from 'echarts-for-react';

const EmissionsComponent: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const ability = useAbility();
    const [filterCompany, setFilterCompany] = useState('companyGlobalView');
    const [filterCompany2, setFilterCompany2] = useState('Company 1');
    const [filterVessel, setFilterVessel] = useState('vesselGlobalView');
    const [filterVessel2, setFilterVessel2] = useState('Vessel 1');
    const [showResolved, setShowResolved] = useState('cox');
    const [isFullScreenGraphicBars, setIsFullScreenGraphicBars] = useState(false);
    const [isFullScreenTable, setIsFullScreenTable] = useState(false);
    const [dataVessels, setDataVessels] = useState([{ Tembarcacion: "", IDregistro: "", Nembarcacion: 0, Tipo: 0, AManufactura: 0, Ffabricacion: "", Tamanno: 0, Foto: "", Nempresa: 0 }]);
    const [dataVesselsTembarcacion, setDataVesselsTembarcacion] = useState<string[]>([]);
    const [dataCompany, setDataCompany] = useState([{ Nempresa: 0, Tempresa: "", CIF: "", Tdireccion: "", Latitud: "", Longitud: "", Tcontacto: "", PhoneContacto: "", Tcorreo: "", Lenguaje: "", Falta: "" }]);
    const [dataCompanyTempresa, setDataCompanyTempresa] = useState<string[]>([]);
    const [dataTable, setDataTable] = useState([{ Date: "", cox: "", nox: "", sox: "" }]);
    const [coxSaved, setCoxSaved] = useState("");
    const [noxSaved, setNoxSaved] = useState("");
    const [soxSaved, setSoxSaved] = useState("");
    const { t } = useTranslation();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const [startDate, setStartDate] = useState<Date | null>(sevenDaysAgo);
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    /**
     * Obtiene información de emisiones, empresas y embarcaciones desde la API.
     * - Llama a `getInformationEmissions` para obtener los datos de emisiones.
     * - Obtiene las listas de embarcaciones y empresas.
     * - Filtra los tipos de embarcación (`Tembarcacion`) y empresa (`Tempresa`) únicos.
     */
    const getInformation = useCallback(async () => {

        try {
            getInformationEmissions(startDate?.toISOString() ?? '', endDate?.toISOString() ?? '', 0, 0);

            const responseVessel = await getVessel();

            const responseCompany = await getCompany();

            setDataVessels(responseVessel.data.data as Vessels[]);
            const listTembarcacion = (responseVessel.data.data as Vessels[])?.length ? Array.from(new Set((responseVessel.data.data as Vessels[]).map(item => item.Tembarcacion))).sort() : [];
            setDataVesselsTembarcacion(listTembarcacion);

            setDataCompany(responseCompany.data.data as Company[]);
            const empresasConVessels = new Set((responseVessel.data.data as Vessels[]).map(vessel => vessel.Nempresa));
            const empresasFiltradas = (responseCompany.data.data as Company[]).filter(company => empresasConVessels.has(company.Nempresa));
            const listTempresa = empresasFiltradas.length ? Array.from(new Set(empresasFiltradas.map(item => item.Tempresa))).sort() : [];
            setDataCompanyTempresa(listTempresa);

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Obtiene información de emisiones eléctricas (CO₂, NOₓ, SOₓ) desde la API.
     * @param Finicio - Fecha de inicio en formato ISO.
     * @param Ffinal - Fecha de fin en formato ISO.
     * @param Nempresa - ID de la empresa.
     * @param Nembarcacion - ID de la embarcación.
     */
    const getInformationEmissions = async (Finicio: string, Ffinal: string, Nempresa: number, Nembarcacion: number) => {

        try {

            const body = {
                Nempresa: Nempresa,
                Nembarcacion: Nembarcacion,
                Ffinal: Ffinal,
                Finicio: Finicio
            }

            const responseEmissions = await postEmissions(body);

            if (responseEmissions.status >= 200 && responseEmissions.status < 300) {
                toast.success(`${responseEmissions.data.message}`);
            }

            setDataTable(
                responseEmissions.data.data.electricalVar.map((electrical: Emissions["electricalVar"][number]) => ({
                    Date: electrical.Date.toString(),
                    cox: electrical.co2.toFixed(2),
                    nox: electrical.nox.toFixed(2),
                    sox: electrical.sox.toFixed(2)
                }))
            );
            setCoxSaved(responseEmissions.data.data.co2Saved.toFixed(2));
            setNoxSaved(responseEmissions.data.data.noxSaved.toFixed(2));
            setSoxSaved(responseEmissions.data.data.soxSaved.toFixed(2));

        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Carga toda la información al montar el componente.
     */
    useEffect(() => {
        getInformation();
    }, [getInformation]);

    /**
     * Categorías para el gráfico de barras (fechas).
     */
    const barCategories = dataTable.map((item) => item.Date);

    /**
     * Valores para el gráfico de barras según el contaminante seleccionado.
     */
    const barData = dataTable.map((item) =>
        parseFloat(showResolved === 'cox' ? item.cox : showResolved === 'nox' ? item.nox : item.sox)
    );

    /**
     * Descarga los datos del gráfico como archivo XLSX.
     * @param categories - Fechas u otras categorías.
     * @param data - Valores numéricos correspondientes.
     * @param title - Título de la hoja y archivo.
     */
    const downloadDataGraphicAsXLSX = (categories: string[], data: number[], title: string) => {
        const worksheetData = categories.map((category, index) => ({
            Category: category,
            Value: data[index],
        }));
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title);
        XLSX.writeFile(workbook, `${title}.xlsx`);
    };

    /**
     * Descarga la tabla completa de datos (`dataTable`) como archivo XLSX.
     */
    const downloadDataTableAsXLSX = () => {
        const worksheet = XLSX.utils.json_to_sheet(dataTable);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Emissions Table');
        XLSX.writeFile(workbook, 'Emissions Table.xlsx');
    };

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [sortedData, setSortedData] = useState([...dataTable]);
    const totalPage = itemsPerPage === sortedData.length ? 1 : Math.ceil(sortedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = itemsPerPage === sortedData.length ? sortedData.length : startIndex + itemsPerPage;
    const currentData = sortedData.slice(startIndex, endIndex);

    /**
     * Avanza a la siguiente página si aún no se ha alcanzado la última.
     */
    const handleNextPage = () => {
        if (currentPage < totalPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    /**
     * Retrocede a la página anterior si no está en la primera.
     */
    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const [sortConfig, setSortConfig] = useState<{
        key: keyof typeof dataTable[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "Date", direction: "desc" });

    /**
     * Ordena los datos de la tabla por fecha en orden descendente cuando cambia `dataTable`.
     */
    useEffect(() => {
        const sorted = [...dataTable].sort((a, b) => (a.Date < b.Date ? 1 : -1));
        setSortedData(sorted);
    }, [dataTable]);

    /**
     * Ordena la tabla según la columna seleccionada.
     * Si se vuelve a hacer clic en la misma columna, alterna entre ascendente y descendente.
     * @param key - Clave del campo por el que se desea ordenar.
     */
    const sortData = (key: keyof typeof dataTable[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sorted = [...sortedData].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setSortedData(sorted);
    };

    /**
     * Filtra la lista de tipos de embarcación (`Tembarcacion`) según la empresa seleccionada (`filterCompany2`).
     * Si no se encuentra una empresa, muestra todos los tipos disponibles.
     */
    useEffect(() => {
        const filteredDataCompany = dataCompany.filter(item => item.Tempresa === filterCompany2);

        if (filteredDataCompany.length > 0) {
            const filteredDataVessels = dataVessels.filter(item => item.Nempresa === filteredDataCompany[0].Nempresa);
            const listTembarcacion = filteredDataVessels?.length ? Array.from(new Set(filteredDataVessels.map(item => item.Tembarcacion))).sort() : [];
            setDataVesselsTembarcacion(listTembarcacion);
        } else {
            const listTembarcacion = dataVessels?.length ? Array.from(new Set(dataVessels.map(item => item.Tembarcacion))).sort() : [];
            setDataVesselsTembarcacion(listTembarcacion);
        }

    }, [filterCompany2, dataCompany, dataVessels]);

    const { isDarkTheme } = useTheme();

    /**
     * Genera las opciones de configuración para el gráfico de barras utilizando el método `createBarChartOption2`.
     * 
     * - `barData`: datos numéricos a mostrar.
     * - `barCategories`: categorías del eje X.
     * - El tercer parámetro define la leyenda basada en el valor de `showResolved`:
     *    - "cox" → leyenda: COx (kg)
     *    - "nox" → leyenda: NOx (kg)
     *    - Otro → leyenda: SOx (kg)
     * - `isDarkTheme`: define si el tema oscuro está activo para ajustar colores.
     */
    const optionsBar = ChartOptionsBuilder.createBarChartOption2(barData, barCategories, showResolved === "cox" ? ['COx (kg)'] : showResolved === "nox" ? ['NOx (kg)'] : ['SOx (kg)'], isDarkTheme);

    /**
     * Aplica los filtros seleccionados para obtener la información de emisiones.
     * 
     * - Verifica que las fechas de inicio y fin estén definidas, de lo contrario muestra un error.
     * - Obtiene el ID de empresa (`empresaID`) y embarcación (`embarcacionID`) según el filtro seleccionado:
     *    - Si `filterCompany` es "byCompany", busca la empresa por nombre (`filterCompany2`) en `dataCompany`.
     *    - Si no, usa el valor almacenado en la cookie `Nempresa`.
     *    - Lo mismo para la embarcación, usando `filterVessel`, `filterVessel2`, `dataVessels` y la cookie `Nembarcacion`.
     * - Finalmente, llama a `getInformationEmissions` con las fechas en formato ISO y los IDs obtenidos.
     */
    const handleApplyFilters = () => {
        if (!startDate || !endDate) {
            toast.error(`${t('incidents.pleaseCompleteAllFields')}.`);
            return;
        }

        let empresaID: number = 0;
        let embarcacionID: number = 0;

        const empresaCookie = Cookies.get("Nempresa");
        const embarcacionCookie = Number(Cookies.get("Nembarcacion"));

        if (filterCompany === "byCompany") {
            const companyMatch = dataCompany.find(item => item.Tempresa === filterCompany2);
            empresaID = companyMatch ? companyMatch.Nempresa : 0;
        } else if (empresaCookie && !isNaN(Number(empresaCookie))) {
            empresaID = Number(empresaCookie);
        }

        if (filterVessel === "byVessel") {
            const vesselMatch = dataVessels.find(item => item.Tembarcacion === filterVessel2);
            embarcacionID = vesselMatch ? vesselMatch.Nembarcacion : 0;
        } else if (embarcacionCookie && !isNaN(Number(embarcacionCookie))) {
            embarcacionID = Number(embarcacionCookie);
        }

        getInformationEmissions(
            startDate.toISOString(),
            endDate.toISOString(),
            empresaID,
            embarcacionID
        );
    };

    return (
        <div className='containerEmissions'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('emissions.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        <h4>{t('emissions.emissionsData')}</h4>
                    </div>

                    <div className='filter'>
                        <div className='calendar'>
                            <DatePicker
                                selected={startDate}
                                onChange={(dates: [Date | null, Date | null]) => {
                                    const [start, end] = dates;
                                    setStartDate(start);
                                    setEndDate(end);
                                }}
                                startDate={startDate}
                                endDate={endDate}
                                selectsRange
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={new Date()}
                            />
                        </div>

                        {ability.can('read', 'AdminPage') && (
                            <select
                                value={filterCompany}
                                onChange={(e) => {
                                    setFilterCompany(e.target.value);
                                    setFilterCompany2(dataCompanyTempresa[0]);
                                    setFilterVessel('vesselGlobalView');
                                }}
                            >
                                <option value="companyGlobalView">{t('emissions.companyGlobalView')}</option>
                                <option value="byCompany">{t('emissions.byCompany')}</option>
                            </select>
                        )}

                        {ability.can('read', 'AdminPage') && filterCompany === "byCompany" && (
                            <select
                                value={filterCompany2}
                                onChange={(e) => {
                                    setFilterCompany2(e.target.value);
                                    setFilterVessel('vesselGlobalView');
                                }}
                            >
                                {dataCompanyTempresa.map((company) => (
                                    <option key={company} value={company}>{company}</option>
                                ))}
                            </select>
                        )}

                        {(ability.can('read', 'AdminPage') || ability.can('read', 'ManagerPage')) && (
                            <select
                                value={filterVessel}
                                onChange={(e) => {
                                    setFilterVessel(e.target.value);
                                    setFilterVessel2(dataVesselsTembarcacion[0]);
                                }}
                            >
                                <option value="vesselGlobalView">{t('emissions.vesselGlobalView')}</option>
                                <option value="byVessel">{t('emissions.byVessel')}</option>
                            </select>
                        )}

                        {(ability.can('read', 'AdminPage') || ability.can('read', 'ManagerPage')) && filterVessel === "byVessel" && (
                            <select
                                value={filterVessel2}
                                onChange={(e) => {
                                    setFilterVessel2(e.target.value);
                                }}
                            >
                                {dataVesselsTembarcacion.map((vessel) => (
                                    <option key={vessel} value={vessel}>{vessel}</option>
                                ))}
                            </select>
                        )}
                        <button onClick={handleApplyFilters}>
                            <i className="fas fa-search"></i>
                        </button>
                    </div>

                    <div className='separator1'>
                        <div className='card'>
                            <div>
                                <span>COx {t('emissions.saved')}</span>
                                <span className='icon'><i className="fas fa-bolt"></i></span>
                            </div>
                            <span>{coxSaved} kg</span>
                        </div>

                        <div className='card'>
                            <div>
                                <span>NOx {t('emissions.saved')}</span>
                                <span className='icon'><i className="fa-solid fa-gauge-high"></i></span>
                            </div>
                            <span>{noxSaved} kg</span>
                        </div>

                        <div className='card'>
                            <div>
                                <span>SOx {t('emissions.saved')}</span>
                                <span className='icon'>$</span>
                            </div>
                            <span>{soxSaved} kg</span>
                        </div>
                    </div>

                    <div className='buttons' >
                        <button className={`btn ${showResolved === "cox" && 'active'}`} onClick={() => setShowResolved('cox')}>
                            COx
                        </button>
                        <button className={`btn ${showResolved === "nox" && 'active'}`} onClick={() => setShowResolved('nox')}>
                            NOx
                        </button>
                        <button className={`btn ${showResolved === "sox" && 'active'}`} onClick={() => setShowResolved('sox')}>
                            SOx
                        </button>
                    </div>

                    <div className='separator2'>
                        <div className={`card ${isFullScreenGraphicBars ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{showResolved === "cox" ? ['COx'] : showResolved === "nox" ? ['NOx'] : ['SOx']} {t('emissions.emissions')}</span>
                                <div className='icons'>
                                    <i
                                        className={`fas ${isFullScreenGraphicBars ? 'fa-compress' : 'fa-expand'}`}
                                        onClick={() => setIsFullScreenGraphicBars(!isFullScreenGraphicBars)}
                                        title={isFullScreenGraphicBars ? "Salir de Pantalla Completa" : "Pantalla Completa"}
                                    ></i>
                                    <i
                                        className="fas fa-file-excel"
                                        onClick={() => downloadDataGraphicAsXLSX(barCategories, barData, `${showResolved === "cox" ? "COx" : showResolved === "nox" ? "NOx" : "SOx"} Emissions`)}
                                        title="Descargar como Excel"
                                    ></i>
                                </div>
                            </div>
                            <div className='graphic'>
                                <div style={{ height: '100%', width: '100%' }}>
                                    <ReactECharts
                                        option={optionsBar}
                                        style={{ height: '100%', width: '99.9%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={`card ${isFullScreenTable ? 'expanded' : ''}`}>
                            <div className='caption'>
                                <span className='title'>{t('emissions.dataTable')}</span>
                                <div className='right'>
                                    <button className="btn btn-dark download-btn" onClick={() => downloadDataTableAsXLSX()}>
                                        <i className="fas fa-download"></i>
                                    </button>

                                    <button className="btn btn-dark fullscreen-btn" onClick={() => {
                                        setIsFullScreenTable(!isFullScreenTable);
                                    }}>
                                        <i className={`fas ${isFullScreenTable ? 'fa-compress' : 'fa-expand'}`}></i>
                                    </button>
                                </div>
                            </div>
                            <div className='tableConsumption'>
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th onClick={() => sortData("Date")}>
                                                {t('emissions.date')} {sortConfig.key === "Date" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("cox")}>
                                                COx {sortConfig.key === "cox" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("nox")}>
                                                NOx {sortConfig.key === "nox" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                            <th onClick={() => sortData("sox")}>
                                                SOx {sortConfig.key === "sox" ?
                                                    (sortConfig.direction === "asc" ?
                                                        (<i className="fas fa-arrow-up" />) :
                                                        (<i className="fas fa-arrow-down" />)
                                                    ) : (
                                                        <i className="fa-solid fa-arrow-down-a-z"></i>
                                                    )}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.Date}</td>
                                                <td>{row.cox}</td>
                                                <td>{row.nox}</td>
                                                <td>{row.sox}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="pagination">
                                <button className="btn btn-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                                    <i className="fas fa-chevron-left"></i> {t('emissions.previous')}
                                </button>
                                <div>
                                    <span>{t('emissions.page')} {currentPage} {t('emissions.of')} {totalPage},</span>
                                    <div className="pagination-controls">
                                        <span>{t('emissions.rowsPerPage')}:</span>
                                        <select
                                            className="form-control rows-select"
                                            value={
                                                itemsPerPage === dataTable.length && ![5, 10, 15, 50].includes(itemsPerPage)
                                                    ? "All"
                                                    : itemsPerPage
                                            }
                                            onChange={(e) => {
                                                const value = e.target.value === "All" ? dataTable.length : parseInt(e.target.value, 10);
                                                setItemsPerPage(value);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="15">15</option>
                                            <option value="50">50</option>
                                            <option value="All">{t('emissions.all')}</option>
                                        </select>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleNextPage}
                                    disabled={currentPage === Math.ceil(dataTable.length / itemsPerPage)}
                                >
                                    {t('emissions.next')} <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

};

export default EmissionsComponent;